/*
================================================================================================
  The Common Helpers Manifesto: (Last updated 11/10/2020 by juanireyes)
  PLEASE, ONLY FULL JAVASCRIPT METHODS, NO NFL CODING HERE! I'M WATCHING YOU!!!
  This file includes the global helpers that can be used in EVERY component when needed, please,
  if you add a new method here, add a small description about its functionality so the team can
  know where and how to reuse it. Also if its params are not very clear, add @params to it.
================================================================================================
*/

/* String Helpers */

export const isBlankString = str => str.trim() === '';

/**
 * Method that ellipse (add '...' at the end) a string if it exceed the length param.
 * @param {string} str The current text to truncate.
 * @param {number=} length Maximum length allowed
 * @param {string=} ending String to put at the end of the truncate.
 */
export const text_truncate = (str = '', length = 100, ending = '...') => {
  if (str.length > length) {
    return str.substring(0, length - ending.length) + ending;
  } else {
    return str;
  }
};

/**
 * Set the first letter of string into uppercase
 * @param {string} string String to be converted, e.g. test -> Test
 */
export const firstLetterToUpperCase = string => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const getMetricValue = (isCounts, relative, custom, counts, keys) => {
  const base = isCounts ? counts : keys;
  if (relative) return `${base}Relative`;
  if (custom) return `${base}Custom`;
  return base;
};
/* End of String Helpers */

/* Date/Time Helpers */

/**
 * YYYY-MM-DD format into a Date
 * @param {string | Date} str String date, e.g 2017-02-20
 */
export const YYYYMMDDToDate = (str) => {
  const dates = str.split('-').map(d => +d);
  return new Date(dates[0], dates[1] - 1, dates[2], 0, 0, 0);
};

/**
 * Converts milliseconds to readable time (HH:MM:SS)
 * @param {number} duration Time in miliseconds
 */
export const msToTime = duration => {
  if (+duration === -1) {
    return '-';
  }
  let seconds = ~~((duration / 1000) % 60)
    , minutes = ~~((duration / (1000 * 60)) % 60)
    , hours = ~~((duration / (1000 * 60 * 60)));
  hours = (hours < 10) ? '0' + hours : hours;
  minutes = (minutes < 10) ? '0' + minutes : minutes;
  seconds = (seconds < 10) ? '0' + seconds : seconds;
  return hours + ':' + minutes + ':' + seconds;
};

/**
 * Converts minutes to miliseconds, and then calls the msToTime method.
 * @param {number} mins Time in minutes.
 */
export const minsToTime = mins => {
  const duration = mins * 1000;
  const cad = msToTime(duration);
  return cad;
};

const updateSecMinHours = (minor, major) => {
  if (minor > 59) {
    major += ~~(minor / 60);
    minor = minor % 60;
  }
  return [minor, major];
};

const updatePassed24Hours = hour => {
  if (hour > 23) {
    return hour - 24;
  }
  return hour;
};

const roundUnderTenTimeDigits = number => {
  return ('0' + number).slice(-2);
};

export const getPracticeTimestamp = (secs, timestamp) => {
  const date_obj = new Date(timestamp);
  let tsHour = date_obj.getHours();
  let tsMinutes = date_obj.getMinutes();
  let tsSeconds = date_obj.getSeconds() + secs > -1 ? date_obj.getSeconds() + secs : 0;

  [tsSeconds, tsMinutes] = updateSecMinHours(tsSeconds, tsMinutes);
  [tsMinutes, tsHour] = updateSecMinHours(tsMinutes, tsHour);
  tsHour = updatePassed24Hours(tsHour);

  return roundUnderTenTimeDigits(tsHour) + ':' + roundUnderTenTimeDigits(tsMinutes) + ':' + roundUnderTenTimeDigits(tsSeconds);
};

/**
 * Get the seconds between 2 timestamps
 * @param {timestamp} startTime Start ts
 * @param {timestamp} endTime End ts
 */
export const tsDifferenceInSecs = (startTime, endTime) => {
  return ~~((endTime - startTime) / 1000);
};

/* End of Date/Time Helpers */

/* Numeric Helpers */

export const round = number => Math.round(number);

export const toPercentage = number => fixDecimals(number * 100, 1);

export const stringIsNumber = str => /^\d+$/.test(str);

export const stringIsDecimal = str => /^[+-]?[0-9]{1,9}(?:\\.[0-9]{1,2})?$/.test(str);

export const isNonNegativeNumber = number => !isNaN(number) && +number >= 0;

/**
 * Given the qty of decimals wanted, this method rounds it more perfectly than a common round.
 * @param {number} number Number to be rounded
 * @param {number} decimals Quantity of decimals wanted for the rounding
 */
export const fixDecimals = (number, decimals) => {
  if (Number.isNaN(+number)) {
    return '-';
  }
  const splitedNumber = (+parseFloat(number).toFixed(decimals)).toString().split('.');
  let newNumber = (+parseFloat(number).toFixed(decimals)).toString();
  let decimalSize = 0;
  if (splitedNumber.length === 1) {
    if (decimals > 0) {
      newNumber += '.';
    }
  } else {
    decimalSize = splitedNumber[1].length;
  }
  for (let findex = 0; findex < decimals - decimalSize; findex++) {
    newNumber += '0';
  }
  return newNumber;
  //return +parseFloat(number).toFixed(f)
};

/**
 * Remove decimals from seconds pointing at the down value
 * @param {number} secs Seconds to be formated e.g. 1205 secs (120 secs w/ 5 decimals)
 */
export const removeDecimalsFromSecs = secs => {
  return ~~(secs / 10);
};

/**
 * Convert the Feets per Second measurement into Miles per Hour.
 * @param {number} fps Feets per Second value
 */
export const fpsToMphConverter = fps => {
  return fixDecimals(fps / 1.46666667, 1);
};

/* End of Numeric Helpers */

/* Other Helpers */

export const arrayIsEmpty = array => Array.isArray(array) && array.length === 0;

export const isEmptyObject = object => object && Object.keys(object).length === 0 && object.constructor === Object;

export const getObjectLength = object => object && Object.keys(object).length;

/**
 * Method used for sorting an array
 * @param {Array} arr1
 * @param {Array} arr2
 */
export const arraySort = (arr1, arr2) => {
  for (let i = 0; i < arr1.length && i < arr2.length; ++i) {
    if (!Number.isNaN(+arr1[i]) && !Number.isNaN(+arr2[i])) {
      if (+arr1[i] !== +arr2[i]) {
        return arr1[i] - arr2[i];
      }
    } else {
      if (arr1[i] !== arr2[i]) {
        return (arr1[i].localeCompare(arr2[i]));
      }
    }
  }
  return arr1.length - arr2.length;
};

/**
 * Sort two values in an Object Array
 * https://www.sitepoint.com/sort-an-array-of-objects-in-javascript/
 * @param {string} prop1 Property to be sorted on object
 * @param {string=} prop2 On case of a nested object (prop1.prop2) add this prop.
 * @param {number=} order Sort order (1 ascendent or -1 descendent)
 */
export const compareObjectValues = (prop1, prop2 = null, order = 1) => {
  return function innerSort(a, b) {
    if (!a.hasOwnProperty(prop1) || !b.hasOwnProperty(prop1)) {
      // property doesn't exist on neither object
      return 0;
    }

    const nestedA = prop2 ? a[prop1][prop2] : a[prop1];
    const nestedB = prop2 ? b[prop1][prop2] : b[prop1];

    const varA = (typeof nestedA === 'string')
      ? nestedA.toUpperCase() : nestedA;
    const varB = (typeof nestedB === 'string')
      ? nestedB.toUpperCase() : nestedB;

    let comparison = 0;
    if (varA > varB) {
      comparison = 1;
    } else if (varA < varB) {
      comparison = -1;
    }
    return comparison * order;
  };
};

/**
 * Array to object conversion.
 * @param {Array} array The array to be converted.
 * @param {string} key Value of the array that will be saved as the object key.
 */
export const convertArrayToObject = (array, key) => {
  const initialValue = {};
  return array.reduce((obj, item) => {
    return {
      ...obj,
      [item[key]]: item
    };
  }, initialValue);
};

// review mdfk - if this is on a build go and find Juani LOL
export const setArrayFromIndex = (array, reqSec) => {
  const indArray = {};
  array && array.forEach((blink, i) => {
    indArray[i + reqSec] = blink;
  });
  return indArray;
};

/**
 * Method to remove the first 1...n values of an object.
 * @param {Object} obj Object to be reduced
 * @param {number} n Numeric limit from the reduced object, from 1 to n
 */
export const reduceObjectSize = (obj, n) => {
  const data_obj = { ...obj };
  Object.keys(data_obj).forEach((key, index) => {
    if (index < n) {
      delete data_obj[key];
    }
  });
  return data_obj;
};

/**
 * Super userful method that allows to separate iterations into their own instances.
 *
 * Based on:
 * https://dev.to/chromiumdev/cancellable-async-functions-in-javascript-5gp7
 *
 * Example to learn more about generators:
 * https://developer.mozilla.org/es/docs/Web/JavaScript/Referencia/Objetos_globales/Generador
 *
 * @param {Generator} generator The receiving generator with its iterations
 * @param {Function} callback Callback method fired in case the generator is overwritten
 */
export const generatorHandler = (generator, callback) => {
  let globalNonce;
  return async function (...args) {
    const localNonce = globalNonce = {};

    const iter = generator(...args);
    let resumeValue;
    for (;;) {
      const n = iter.next(resumeValue);
      if (n.done) return n.value;

      resumeValue = await n.value;
      if (localNonce !== globalNonce) {
        if (callback) callback(...args);
        return;
      }
    }
  };
};

const getImageInBase64 = file => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onerror = (error) => reject(error);
    reader.onload = () => resolve(reader.result);
  });
};

/**
 * Ask Herbert for further information.
 * Convert a file image into a base64 using FileReader
 * @param {File} fileImg The file image to be converted
 */
export const uploadFileImage = fileImg => {
  return getImageInBase64(fileImg)
    .then(data => data)
    .catch(() => null);
};

export const sortCustomers = (field) =>{
  return function(a, b) {
    return (a[field] > b[field]) - (a[field] < b[field]);
 };

};

export const sortCustomersPlayers = (field) =>{
  return function(a, b) {
  const jerseyA =  a[field].toString().replace(/\D/g, '');
  const jerseyB =  b[field].toString().replace(/\D/g, '');
  const jerseyLetterA = a[field].toString().replace(jerseyA, '');
  const jerseyLetterB = b[field].toString().replace(jerseyB, '');
  if (+jerseyA !== +jerseyB) {
    return +jerseyA- +jerseyB;
     
  }
  return jerseyLetterA.localeCompare(jerseyLetterB);
 };

};
export const playersJerseySort = (contentA, contentB) => {
  
  const jerseyA =  contentA.jerseyNumber.replace(/\D/g, '');
  const jerseyB =  contentB.jerseyNumber.replace(/\D/g, '');
  const jerseyLetterA = contentA.jerseyNumber.replace(jerseyA, '');
  const jerseyLetterB = contentB.jerseyNumber.replace(jerseyB, '');
  if (+jerseyA !== +jerseyB) {
    return +jerseyB- +jerseyA;
     
  }
  return jerseyLetterB.localeCompare(jerseyLetterA);
  
};
/* End of Other Helpers */

